import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import { type OperationVariables, useMutation } from '@apollo/client'

import { useToast } from 'Components/Toast/useToast'

import type { Tag, TagColor } from './tagging.types'
import type { TaggingEntityAdapter } from './taggingEntityAdapter.types'
import { TagToastContent } from './TagToastContent'
import { useMutationErrorHandler } from './useMutationErrorHandler'

type TagMutationAction = 'add' | 'remove'

interface UseTagMutationActionsOptions<
  TAddData,
  TAddVariables extends OperationVariables,
  TRemoveData,
  TRemoveVariables extends OperationVariables,
> {
  adapter: TaggingEntityAdapter<
    TAddData,
    TAddVariables,
    TRemoveData,
    TRemoveVariables
  >
  getEntityIdsWithTag: (entityIds: string[], tag: Tag) => string[]
  getEntityIdsWithTagName: (entityIds: string[], name: string) => string[]
  onAddCompleted: (affectedEntityIds: string[]) => Promise<void> | void
  onMutationError?: (action: TagMutationAction) => void
  onMutationStart?: (action: TagMutationAction) => void
  onNoAffected?: (action: TagMutationAction) => void
  onRemoveCompleted: (affectedEntityIds: string[]) => Promise<void> | void
  orgId: string
  resolveOrgTagId: (name: string) => Promise<string | undefined>
}

export const useTagMutationActions = <
  TAddData,
  TAddVariables extends OperationVariables,
  TRemoveData,
  TRemoveVariables extends OperationVariables,
>(
  options: UseTagMutationActionsOptions<
    TAddData,
    TAddVariables,
    TRemoveData,
    TRemoveVariables
  >,
) => {
  const {
    adapter,
    getEntityIdsWithTag,
    getEntityIdsWithTagName,
    onAddCompleted,
    onMutationError,
    onMutationStart,
    onNoAffected,
    onRemoveCompleted,
    orgId,
    resolveOrgTagId,
  } = options
  const { hideToast, showToast } = useToast()
  const intl = useIntl()
  const handleMutationError = useMutationErrorHandler()

  const [executeAddTags, { loading: isAddingTag }] = useMutation<
    TAddData,
    TAddVariables
  >(adapter.add.document)

  const [executeRemoveTags, { loading: isRemovingTag }] = useMutation<
    TRemoveData,
    TRemoveVariables
  >(adapter.remove.document)

  const completeAdd = useCallback(
    (affectedEntityIds: string[]) =>
      Promise.resolve(onAddCompleted(affectedEntityIds)).catch(() => {}),
    [onAddCompleted],
  )

  const completeRemove = useCallback(
    (affectedEntityIds: string[]) =>
      Promise.resolve(onRemoveCompleted(affectedEntityIds)).catch(() => {}),
    [onRemoveCompleted],
  )

  const addTag = useCallback(
    (entityIds: string[], name: string, color: TagColor) => {
      if (!orgId) {
        return
      }

      const normalizedName = name.trim().toLowerCase()
      const previouslyTaggedEntityIds = new Set(
        getEntityIdsWithTagName(entityIds, normalizedName).map(String),
      )

      onMutationStart?.('add')
      executeAddTags({
        onCompleted: data => {
          const affectedEntityIds = adapter.add
            .getAffectedEntityIds(data)
            .filter(entityId => !previouslyTaggedEntityIds.has(entityId))

          if (affectedEntityIds.length === 0) {
            onNoAffected?.('add')

            return
          }

          const completionPromise = completeAdd(affectedEntityIds)

          showToast({
            content: (
              <TagToastContent
                message={intl.formatMessage(
                  { id: adapter.messages.added },
                  { count: affectedEntityIds.length, name },
                )}
                onUndo={async () => {
                  await completionPromise

                  let tagId: string | undefined

                  try {
                    tagId = await resolveOrgTagId(name)
                  } catch (error) {
                    handleMutationError(
                      adapter.remove.operationName,
                      adapter.messages.removeError,
                    )(
                      error instanceof Error
                        ? error
                        : new Error('Failed to resolve tag for Undo'),
                    )

                    return
                  }

                  if (!tagId) {
                    handleMutationError(
                      adapter.remove.operationName,
                      adapter.messages.removeError,
                    )(new Error('Failed to resolve tag for Undo'))

                    return
                  }

                  hideToast()
                  onMutationStart?.('remove')
                  executeRemoveTags({
                    onCompleted: () => {
                      completeRemove(affectedEntityIds)
                    },
                    onError: (error, errorOptions) => {
                      onMutationError?.('remove')
                      handleMutationError(
                        adapter.remove.operationName,
                        adapter.messages.removeError,
                      )(error, errorOptions)
                    },
                    variables: adapter.remove.toVariables({
                      entityIds: affectedEntityIds,
                      orgId,
                      tagIds: [tagId],
                    }),
                  })
                }}
              />
            ),
            variant: 'success',
          })
        },
        onError: (error, errorOptions) => {
          onMutationError?.('add')
          handleMutationError(
            adapter.add.operationName,
            adapter.messages.addError,
          )(error, errorOptions)
        },
        variables: adapter.add.toVariables({
          color,
          entityIds,
          name,
          orgId,
        }),
      })
    },
    [
      adapter,
      completeAdd,
      completeRemove,
      executeAddTags,
      executeRemoveTags,
      getEntityIdsWithTagName,
      handleMutationError,
      hideToast,
      intl,
      onMutationError,
      onMutationStart,
      onNoAffected,
      orgId,
      resolveOrgTagId,
      showToast,
    ],
  )

  const removeTag = useCallback(
    (entityIds: string[], tag: Tag) => {
      if (!orgId) {
        return
      }

      const affectedEntityIds = getEntityIdsWithTag(entityIds, tag)

      if (affectedEntityIds.length === 0) {
        onNoAffected?.('remove')

        return
      }

      onMutationStart?.('remove')
      executeRemoveTags({
        onCompleted: () => {
          const completionPromise = completeRemove(affectedEntityIds)

          showToast({
            content: (
              <TagToastContent
                message={intl.formatMessage(
                  { id: adapter.messages.removed },
                  { count: affectedEntityIds.length, name: tag.name },
                )}
                onUndo={async () => {
                  await completionPromise

                  hideToast()
                  onMutationStart?.('add')
                  executeAddTags({
                    onCompleted: () => {
                      completeAdd(affectedEntityIds)
                    },
                    onError: (error, errorOptions) => {
                      onMutationError?.('add')
                      handleMutationError(
                        adapter.add.operationName,
                        adapter.messages.addError,
                      )(error, errorOptions)
                    },
                    variables: adapter.add.toVariables({
                      color: tag.color,
                      entityIds: affectedEntityIds,
                      name: tag.name,
                      orgId,
                    }),
                  })
                }}
              />
            ),
            variant: 'success',
          })
        },
        onError: (error, errorOptions) => {
          onMutationError?.('remove')
          handleMutationError(
            adapter.remove.operationName,
            adapter.messages.removeError,
          )(error, errorOptions)
        },
        variables: adapter.remove.toVariables({
          entityIds: affectedEntityIds,
          orgId,
          tagIds: [tag.id],
        }),
      })
    },
    [
      adapter,
      completeAdd,
      completeRemove,
      executeAddTags,
      executeRemoveTags,
      getEntityIdsWithTag,
      handleMutationError,
      hideToast,
      intl,
      onMutationError,
      onMutationStart,
      onNoAffected,
      orgId,
      showToast,
    ],
  )

  return {
    addTag,
    isAddingTag,
    isRemovingTag,
    removeTag,
  }
}
